import { FC } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { colors } from '../../theme/tokens';

// States of a listing without a design in the XD (gap 8 in DESIGN.md),
// resolved once for tables and cards: loading (centred spinner), error
// (a sentence + "Réessayer") and empty (a sentence, which each screen adapts
// depending on whether there are filters or not).
//
// Returns `null` when there is data to show; the listing then paints its
// rows. That way the caller writes `<ListState … /> ?? rows`.

export type ListStateProps = {
  isLoading: boolean;
  isError: boolean;
  isEmpty: boolean;
  errorText: string;
  emptyText: string;
  onRetry: () => void;
};

export const hasListState = ({
  isLoading,
  isError,
  isEmpty,
}: Pick<ListStateProps, 'isLoading' | 'isError' | 'isEmpty'>): boolean =>
  isLoading || isError || isEmpty;

const ListState: FC<ListStateProps> = ({
  isLoading,
  isError,
  isEmpty,
  errorText,
  emptyText,
  onRetry,
}) => {
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <Typography sx={{ mb: 2 }}>{errorText}</Typography>
        <Button variant="outlined" color="primary" onClick={onRetry}>
          Réessayer
        </Button>
      </Box>
    );
  }

  if (isEmpty) {
    return (
      <Typography sx={{ textAlign: 'center', py: 6, color: colors.muted }}>
        {emptyText}
      </Typography>
    );
  }

  return null;
};

export default ListState;
